import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { ArrowLeft, FileText, Loader2, Paperclip, Play, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Combobox } from '../../components/ui/combobox';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Project } from '../../types/lists.types';
import { AiStatus, Product, ReturnOrderPayload, Yard } from '../../types/stock.types';
import { projectsApi } from '../../utils/lists-api';
import { ApiError } from '../../utils/api';
import { productsApi, returnsApi, stockAiApi, yardsApi } from '../../utils/stock-api';
import { cn } from '../../components/ui/utils';
import GenerateOrderDialog, { GeneratedLine } from './components/GenerateOrderDialog';

const EMPTY: ReturnOrderPayload = {
    yardId: null,
    projectId: null,
    returnDate: new Date().toISOString().slice(0, 10),
    restockingDate: new Date().toISOString().slice(0, 10),
};

/** Matches the documents endpoint's per-request limit. */
const MAX_SOURCE_DOCS = 10;

/**
 * New Return Order (STOCK.md §5.2).
 *
 * A full page rather than a dialog, matching every other create screen in Idarah: the
 * form gets its own URL, so it can be linked, reloaded and backed out of. The list
 * page's "New Return Order" CTA navigates here instead of opening a modal.
 *
 * Routed per yard: `/stock/yards/:yardId/returns/new` locks the yard select to that
 * yard when it is open (inside a yard, offering others would let someone create a
 * return in a yard they just navigated out of); `/stock/returns/new` is the unscoped
 * cross-yard form. When the `:yardId` does not resolve to an open yard — e.g. it is
 * closed — every open yard is offered, because a return can't be raised against a
 * closed one.
 *
 * Two CTAs, and the difference matters: "Create and Start" assigns the return to whoever
 * clicked it and opens at Assigned, ready to count. "Create" leaves it Open and
 * unassigned for someone else to pick up later.
 *
 * Two journeys. "Start blank" is the empty return of old; "From gear sheet (AI)" reads
 * photo(s)/a PDF of the yardie's filled-in count sheet through the shared parse-and-
 * review dialog (§2.5) and creates the return with the reviewed lines attached. The
 * gear sheet IS the physical count, so prefilling from it digitises that count rather
 * than leaking expected figures — no §5.4 blind-count conflict.
 */
export default function ReturnOrderFormPage() {
    const navigate = useNavigate();
    const { yardId: yardIdParam } = useParams<{ yardId?: string }>();
    const scopedYardId = yardIdParam ? Number(yardIdParam) : null;
    const ability = useAbility(AbilityContext);
    const canCreate = ability.can(ACTIONS.CREATE, RESOURCES.STOCK_RETURN_ORDER);

    const [form, setForm] = useState<ReturnOrderPayload>(EMPTY);
    const [saving, setSaving] = useState<'create' | 'start' | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [journey, setJourney] = useState<'blank' | 'gearSheet'>('blank');
    const [aiOpen, setAiOpen] = useState(false);
    const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);
    const [yards, setYards] = useState<Yard[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [productsLoaded, setProductsLoaded] = useState(false);
    /** Lines reviewed and accepted in the AI dialog — created along with the order. */
    const [aiLines, setAiLines] = useState<GeneratedLine[]>([]);
    /** The sheet transcription, kept on the order beside its lines for audit (§2.5). */
    const [rawInputText, setRawInputText] = useState<string | null>(null);
    /** ai_usage_logs row for the parse, sent on create to attribute the spend. */
    const [aiUsageId, setAiUsageId] = useState<number | null>(null);
    /** The sheet file(s) the AI read — stored with the order as evidence (§12.4). */
    const [sheetFiles, setSheetFiles] = useState<File[]>([]);
    /** Source paperwork attached by hand — the storage fallback (§12.4). */
    const [docFiles, setDocFiles] = useState<File[]>([]);
    const docInput = useRef<HTMLInputElement | null>(null);

    const listPath = scopedYardId ? `/stock/yards/${scopedYardId}/returns` : '/stock/returns';

    /** Open yards only — a closed yard is an archive and can't receive a return. */
    const openYards = Array.isArray(yards) ? yards : [];
    // Every project belongs to exactly one yard (FK), so once a yard is chosen only
    // its projects are offered — gear can only return from a project that came out of
    // this yard. Nothing is offered before a yard is picked.
    const selectedYardId = form.yardId ? Number(form.yardId) : null;
    const projectOptions = Array.isArray(projects)
        ? (selectedYardId ? projects.filter((p) => p.yard?.id === selectedYardId) : [])
        : [];
    const projectSelectOptions = projectOptions.map((p) => ({
        label: `${p.projectName} (${p.projectNumber})`,
        value: String(p.id),
    }));

    /**
     * Scoped to an open yard → that yard is the only valid choice, mirroring what the
     * list page enforced when the form was a dialog. Unscoped (or a :yardId that isn't
     * open, e.g. closed) → every open yard is offered.
     */
    const yardOptions = useMemo(() => {
        if (scopedYardId) {
            const scoped = openYards.find((y) => y.id === scopedYardId);
            if (scoped) return [scoped];
        }
        return openYards;
    }, [openYards, scopedYardId]);

    useEffect(() => {
        if (!canCreate) return;
        // §2.6 — decides whether the gear-sheet CTA or the manual-entry microcopy shows.
        stockAiApi.status().then(setAiStatus).catch(() =>
            setAiStatus({ available: false, model: '', message: null }));
        yardsApi.list({ status: 'open' }).then(setYards).catch(() => undefined);
        projectsApi.list({ status: 'active' }).then(setProjects).catch(() => undefined);
    }, [canCreate]);

    // Preselect once the options are in, and only while the user hasn't picked: the
    // scoped yard, or the sole open yard (the common single-yard case).
    useEffect(() => {
        if (form.yardId !== null || openYards.length === 0) return;
        const preset = scopedYardId && openYards.some((y) => y.id === scopedYardId)
            ? scopedYardId
            : (openYards.length === 1 ? openYards[0].id : null);
        if (preset !== null) setForm((f) => ({ ...f, yardId: preset }));
    }, [form.yardId, openYards, scopedYardId]);

    // A project must belong to the chosen yard. If the yard changes (or the projects
    // list arrives after the yard and the selection no longer matches), drop the stale
    // project so the order can't be raised against another yard's project.
    useEffect(() => {
        if (form.projectId === null) return;
        const ok = selectedYardId !== null &&
            projects.some((p) => p.yard?.id === selectedYardId && p.id === form.projectId);
        if (!ok) setForm((f) => ({ ...f, projectId: null }));
    }, [form.projectId, projects, selectedYardId]);

    // The product list is only needed for hand-resolving unmatched lines in the review
    // step, so it loads lazily the first time the gear-sheet journey is picked.
    useEffect(() => {
        if (journey !== 'gearSheet' || productsLoaded) return;
        productsApi.list({ status: 'active' })
            .then((p) => { setProducts(p); setProductsLoaded(true); })
            .catch(() => undefined);
    }, [journey, productsLoaded]);

    /**
     * Merges reviewed AI lines into the draft. Reading another page of the sheet appends
     * rather than replaces, and the same product twice sums its quantities rather than
     * dropping one silently. The sheet files ride along too — they are stored with the
     * order as evidence of what the AI read (§12.4).
     */
    const handleGenerated = (
        generated: GeneratedLine[], raw: string, files: File[], usageId: number | null,
    ) => {
        setRawInputText(raw);
        setAiUsageId(usageId);
        setSheetFiles((prev) => {
            const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
            return [...prev, ...files.filter((f) => !seen.has(`${f.name}:${f.size}`))];
        });
        setAiLines((prev) => {
            const merged = [...prev];
            for (const g of generated) {
                const existing = merged.findIndex((l) => l.productId === g.productId);
                if (existing >= 0) {
                    merged[existing] = {
                        ...merged[existing],
                        quantity: merged[existing].quantity + g.quantity,
                    };
                } else {
                    merged.push(g);
                }
            }
            return merged;
        });
        toast.success(`${generated.length} line(s) added — check them before creating.`);
    };

    /** Switching back to "Start blank" drops the sheet's lines — blank must mean blank. */
    const pickJourney = (next: 'blank' | 'gearSheet') => {
        setJourney(next);
        if (next === 'blank') {
            setAiLines([]);
            setRawInputText(null);
            setAiUsageId(null);
            setSheetFiles([]);
        }
    };

    const handleDocFiles = (picked: FileList | null) => {
        if (!picked?.length) return;
        const incoming = Array.from(picked);
        if (docFiles.length + incoming.length > MAX_SOURCE_DOCS) {
            toast.error(`Up to ${MAX_SOURCE_DOCS} documents — the extra one(s) were ignored.`);
        }
        // Original bytes, no compression — this is the evidentiary copy (§12.4).
        setDocFiles((prev) => [...prev, ...incoming].slice(0, MAX_SOURCE_DOCS));
    };

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.yardId) next.yardId = 'Yard is required';
        if (!form.projectId) next.projectId = 'Project is required';
        if (!form.returnDate) next.returnDate = 'Return Date is required';
        if (!form.restockingDate) next.restockingDate = 'Restocking Date is required';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const submit = async (start: boolean) => {
        if (!validate()) return;
        try {
            setSaving(start ? 'start' : 'create');
            const order = await returnsApi.create({
                ...form,
                start,
                // Reviewed gear-sheet lines ride along with the create — only when any.
                ...(aiLines.length
                    ? {
                        lines: aiLines.map((l) => ({
                            productId: l.productId,
                            quantity: l.quantity,
                            source: 'ai_matched',
                            originalText: l.originalText,
                            aiConfidence: l.confidence ?? undefined,
                        })),
                        rawInputText: rawInputText ?? undefined,
                        aiUsageId,
                    }
                    : {}),
            });
            // §12.4 — the order has to exist before documents can hang off it, so the
            // paperwork uploads right after the create. A failure here must not lose
            // the order: warn and carry on; the detail page can take them later.
            try {
                if (sheetFiles.length) await returnsApi.uploadDocuments(order.id, sheetFiles, 'ai_parse');
                if (docFiles.length) await returnsApi.uploadDocuments(order.id, docFiles, 'upload');
            } catch {
                toast.error(
                    `${order.orderNumber} was created, but the document(s) failed to upload ` +
                    `— attach them again from the order page.`
                );
            }
            toast.success(
                start
                    ? `${order.orderNumber} created and assigned to you`
                    : `${order.orderNumber} created`
            );
            // Started means it's assigned to them and ready to photograph — go straight
            // there rather than making them find it in the list.
            if (start) {
                navigate(scopedYardId
                    ? `/stock/yards/${scopedYardId}/returns/${order.id}`
                    : `/stock/returns/${order.id}`);
            } else {
                navigate(listPath);
            }
        } catch (err: unknown) {
            // The API client wraps validation failures as ApiError with data { field, message }.
            const data = err instanceof ApiError
                ? (err.data as { field?: string; message?: string } | null)
                : null;
            const field = data?.field;
            const message = data?.message
                || (err instanceof Error ? err.message : '')
                || 'Failed to create return order';
            if (field) setErrors({ [field]: message });
            toast.error(message);
        } finally {
            setSaving(null);
        }
    };

    const err = (field: string) =>
        errors[field] ? <p className="text-xs text-destructive mt-1">{errors[field]}</p> : null;

    if (!canCreate) return <AccessRestricted />;

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-2xl">
            {/* Header */}
            <div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(listPath)}
                    className="mb-2 -ml-2"
                >
                    <ArrowLeft size={16} className="mr-1.5" />Return Orders
                </Button>
                <h1 className="text-2xl font-bold">New Return Order</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Gear coming back from a project. You'll photograph the truck first,
                    then count what's on it.
                </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); submit(true); }} className="space-y-4">
                <Card>
                    <CardContent className="p-4 md:p-6 space-y-4">
                        <RadioGroup
                            value={journey}
                            onValueChange={(v) => pickJourney(v as 'blank' | 'gearSheet')}
                            className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                        >
                            <label
                                htmlFor="journey-blank"
                                className={cn(
                                    'flex items-start gap-2 rounded-lg border border-border p-3 cursor-pointer',
                                    journey === 'blank' && 'border-blue-300 bg-blue-50/40',
                                )}
                            >
                                <RadioGroupItem value="blank" id="journey-blank" className="mt-0.5" />
                                <span>
                                    <span className="block text-sm font-medium">Start blank</span>
                                    <span className="block text-xs text-muted-foreground mt-0.5">
                                        Create the return now, count the load later.
                                    </span>
                                </span>
                            </label>
                            <label
                                htmlFor="journey-gear-sheet"
                                className={cn(
                                    'flex items-start gap-2 rounded-lg border border-border p-3 cursor-pointer',
                                    journey === 'gearSheet' && 'border-blue-300 bg-blue-50/40',
                                )}
                            >
                                <RadioGroupItem value="gearSheet" id="journey-gear-sheet" className="mt-0.5" />
                                <span>
                                    <span className="block text-sm font-medium">From gear sheet (AI)</span>
                                    <span className="block text-xs text-muted-foreground mt-0.5">
                                        The AI reads the counted items off a photo of the sheet.
                                    </span>
                                </span>
                            </label>
                        </RadioGroup>

                        <div>
                            <Label htmlFor="return-yard">Yard *</Label>
                            <Select
                                value={form.yardId ? String(form.yardId) : undefined}
                                onValueChange={(v) => setForm({ ...form, yardId: Number(v) })}
                            >
                                <SelectTrigger id="return-yard">
                                    <SelectValue placeholder={
                                        yardOptions.length ? 'Select a yard' : 'No open yards available'
                                    } />
                                </SelectTrigger>
                                <SelectContent>
                                    {yardOptions.map((y) => (
                                        <SelectItem key={y.id} value={String(y.id)}>
                                            {y.yardName} ({y.yardCode})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {err('yardId')}
                        </div>

                        <div>
                            <Label>Project *</Label>
                            <Combobox
                                options={projectSelectOptions}
                                value={form.projectId ? String(form.projectId) : undefined}
                                onChange={(v) => setForm({ ...form, projectId: v ? Number(v) : null })}
                                placeholder={form.yardId === null
                                    ? 'Select a yard first'
                                    : (projectOptions.length ? 'Select a project' : 'No projects for this yard')}
                                searchPlaceholder="Search projects..."
                                emptyMessage="No projects found."
                            />
                            {err('projectId')}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="return-date">Return Date *</Label>
                                <Input
                                    id="return-date"
                                    type="date"
                                    value={form.returnDate}
                                    onChange={(e) => setForm({ ...form, returnDate: e.target.value })}
                                />
                                <p className="text-xs text-muted-foreground mt-1">When the gear physically arrived.</p>
                                {err('returnDate')}
                            </div>
                            <div>
                                <Label htmlFor="restocking-date">Restocking Date *</Label>
                                <Input
                                    id="restocking-date"
                                    type="date"
                                    value={form.restockingDate}
                                    onChange={(e) => setForm({ ...form, restockingDate: e.target.value })}
                                />
                                <p className="text-xs text-muted-foreground mt-1">When it's counted back into stock.</p>
                                {err('restockingDate')}
                            </div>
                        </div>

                        {journey === 'gearSheet' && (
                            <div className="border-t border-border pt-4">
                                <Label className="mb-2 block">Counted lines</Label>
                                <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2">
                                    <p className="text-xs font-medium text-blue-900">
                                        Photograph the filled gear sheet (or upload a PDF of it) — the AI
                                        reads the counted items for you to review.
                                    </p>
                                    {aiStatus !== null && !aiStatus.available ? (
                                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                            Generate with AI is currently down — start blank and add the
                                            lines on the detail page instead.
                                        </p>
                                    ) : (
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="text-xs text-muted-foreground">
                                                Nothing is saved until you create the return.
                                            </p>
                                            <Button type="button" size="sm" onClick={() => setAiOpen(true)}>
                                                <Sparkles size={14} className="mr-1.5" />Read a gear sheet
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Lines accepted in the review step, shown as they will be created. */}
                                {aiLines.length > 0 && (
                                    <div className="rounded-lg border border-border overflow-hidden mt-2">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Product</TableHead>
                                                    <TableHead className="w-[80px]">Qty</TableHead>
                                                    <TableHead className="w-[40px]" />
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {aiLines.map((line) => (
                                                    <TableRow key={line.productId}>
                                                        <TableCell className="whitespace-normal">
                                                            {line.name}{' '}
                                                            <span className="text-muted-foreground font-mono text-xs">
                                                                ({line.sku})
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>{line.quantity}</TableCell>
                                                        <TableCell>
                                                            <Button
                                                                type="button" variant="ghost" size="icon"
                                                                aria-label="Remove line"
                                                                onClick={() => setAiLines((prev) =>
                                                                    prev.filter((l) =>
                                                                        l.productId !== line.productId))}
                                                            >
                                                                <X size={16} />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* §12.4 — source paperwork: the storage fallback, plus the sheet
                            the AI read (tagged). Stored with the order as the source of
                            record; the original bytes go up, no compression. */}
                        <div className="border-t border-border pt-4">
                            <Label className="mb-2 block">
                                Source documents <span className="font-normal text-muted-foreground">(optional)</span>
                            </Label>
                            <p className="text-xs text-muted-foreground mb-2">
                                Attach the paperwork behind this return — PDFs or photos — so the
                                original is never lost.
                            </p>
                            <input
                                ref={docInput}
                                type="file"
                                accept="application/pdf,image/*"
                                multiple
                                className="hidden"
                                onChange={(e) => {
                                    handleDocFiles(e.target.files);
                                    // Reset so picking the same file again still fires onChange.
                                    e.target.value = '';
                                }}
                            />
                            <Button type="button" variant="outline" size="sm"
                                    onClick={() => docInput.current?.click()}>
                                <Paperclip size={14} className="mr-1.5" />Attach documents
                            </Button>
                            {(sheetFiles.length > 0 || docFiles.length > 0) && (
                                <div className="divide-y divide-border border border-border rounded-lg mt-2 bg-white">
                                    {([
                                        ...sheetFiles.map((f, i) => ({
                                            file: f, tag: 'AI sheet', key: `sheet-${i}`,
                                            remove: () => setSheetFiles((prev) =>
                                                prev.filter((_, j) => j !== i)),
                                        })),
                                        ...docFiles.map((f, i) => ({
                                            file: f, tag: null as string | null, key: `doc-${i}`,
                                            remove: () => setDocFiles((prev) =>
                                                prev.filter((_, j) => j !== i)),
                                        })),
                                    ]).map(({ file, tag, key, remove }) => (
                                        <div key={key} className="flex items-center gap-3 p-2">
                                            <span className="w-10 h-10 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                                                <FileText size={16} className="text-muted-foreground" />
                                            </span>
                                            <span className="text-sm truncate flex-1">{file.name}</span>
                                            {tag && (
                                                <span className="text-[10px] font-medium text-blue-700 bg-blue-50 rounded px-1.5 py-0.5 shrink-0">
                                                    {tag}
                                                </span>
                                            )}
                                            <Button type="button" variant="ghost" size="icon"
                                                    aria-label="Remove document" onClick={remove}>
                                                <X size={16} />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => submit(false)}
                        disabled={saving !== null}
                        className="w-full md:w-auto"
                    >
                        {saving === 'create' && <Loader2 size={16} className="animate-spin mr-2" />}
                        Create
                    </Button>
                    <Button
                        type="submit"
                        disabled={saving !== null}
                        className="w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                    >
                        {saving === 'start'
                            ? <Loader2 size={16} className="animate-spin mr-2" />
                            : <Play size={16} className="mr-2" />}
                        Create and Start
                    </Button>
                </div>
            </form>
            <p className="text-xs text-muted-foreground text-right">
                <strong>Create and Start</strong> assigns it to you now. <strong>Create</strong> leaves it
                unassigned for someone else to pick up.
            </p>

            <GenerateOrderDialog
                open={aiOpen}
                onOpenChange={setAiOpen}
                products={products}
                onConfirm={handleGenerated}
                title="Read Gear Sheet"
                initialInput="upload"
            />
        </div>
    );
}
